import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, useParams } from "react-router-dom";
import { Tv, Zap, Clock, User, LogOut, Menu, X, MessageCircle, MessageSquare } from "lucide-react";
import { api, logoutAndRedirect, getActiveSubdomain } from "@/lib/api";
import { toast } from "sonner";
import { useFCM } from "@/hooks/useFCM";

import CustomerDashboard from "./Dashboard";
import Recharge from "./Recharge";
import History from "./History";
import Profile from "./Profile";
import AIChat from "./AIChat";
import Complaints from "./Complaints";

export default function CustomerPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { subdomain: paramSubdomain } = useParams();
  const activeSubdomain = getActiveSubdomain();
  const subdomain = activeSubdomain || paramSubdomain;
  const base = activeSubdomain ? "/customer" : `/site/${subdomain}/customer`;

  // Auto-register FCM push token (silent — user may be prompted by browser)
  useFCM({ autoRequest: true, showToasts: true });

  const nav = [
    { to: base,               label: "Home",    icon: Tv,    end: true },
    { to: `${base}/recharge`, label: "Recharge", icon: Zap },
    { to: `${base}/history`,  label: "History",  icon: Clock },
    { to: `${base}/complaints`, label: "Complaints", icon: MessageSquare },
    { to: `${base}/profile`,  label: "Profile",  icon: User },
  ];

  const logout = () => { logoutAndRedirect(); };

  return (
    <div className="min-h-screen bg-slate-50 font-body">
      {/* Top Nav */}
      <nav className="fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-primary to-brand-accent flex items-center justify-center">
              <Tv className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-brand-nav text-lg">My DTH</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setChatOpen(v => !v)} className="p-2 rounded-lg hover:bg-brand-primary/10 text-brand-primary transition-colors">
              <MessageCircle className="w-5 h-5" />
            </button>
            <button onClick={() => setMenuOpen(v => !v)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="max-w-lg mx-auto px-4 pb-3 border-t border-slate-100">
            <button onClick={logout} className="mt-2 w-full flex items-center gap-2 text-sm text-red-500 hover:text-red-700 py-2">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        )}
      </nav>

      {/* Page Content */}
      <div className="pt-14 pb-20 max-w-lg mx-auto px-4">
        <Routes>
          <Route index element={<CustomerDashboard />} />
          <Route path="recharge" element={<Recharge />} />
          <Route path="history" element={<History />} />
          <Route path="complaints" element={<Complaints />} />
          <Route path="profile" element={<Profile />} />
        </Routes>
      </div>

      {/* AI Chat Overlay */}
      {chatOpen && <AIChat onClose={() => setChatOpen(false)} />}

      {/* Bottom Tab Bar */}
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 safe-area-bottom">
        <div className="max-w-lg mx-auto flex">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to} to={to} end={end}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center py-2 text-xs font-medium transition-colors ${isActive ? "text-brand-primary" : "text-slate-400 hover:text-slate-600"}`
              }
            >
              <Icon className="w-5 h-5 mb-0.5" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
